import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  InputAdornment,
  useTheme,
  alpha
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { searchQuestions } from '../../services/questionService';

const SearchDialog = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    let cancelled = false;

    if (searchQuery) {
      searchQuestions(searchQuery, 8)
        .then((items) => {
          if (!cancelled) setResults(items);
        })
        .catch((error) => {
          console.error('Search failed:', error);
          if (!cancelled) setResults([]);
        });
    } else {
      setResults([]);
    }

    return () => {
      cancelled = true;
    };
  }, [searchQuery]);

  const handleResultClick = (result) => {
    navigate(`/questions/${result.path}`);
    onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search questions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              bgcolor: theme.palette.mode === 'light' 
                ? alpha(theme.palette.common.black, 0.04)
                : alpha(theme.palette.common.white, 0.04)
            }
          }}
        />
      </Box>

      <DialogContent sx={{ pt: 0 }}>
        <AnimatePresence>
          {results.length > 0 ? (
            <List>
              {results.map((result) => (
                <motion.div
                  key={result.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <ListItem
                    button
                    onClick={() => handleResultClick(result)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'light'
                          ? alpha(theme.palette.primary.main, 0.08)
                          : alpha(theme.palette.primary.main, 0.16)
                      }
                    }}
                  >
                    <ListItemText
                      primary={result.title}
                      secondary={result.category}
                      primaryTypographyProps={{
                        variant: 'subtitle1',
                        fontWeight: 500
                      }}
                    />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          ) : searchQuery && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No results found for "{searchQuery}"
              </Typography>
            </Box>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
